import { CdkScrollable } from '@angular/cdk/scrolling';
import {
    booleanAttribute,
    ChangeDetectionStrategy,
    Component,
    computed,
    contentChildren,
    effect,
    HostListener,
    inject,
    input,
    model,
    OnDestroy,
} from '@angular/core';

import { ModalContent } from './modal-content';
import { ModalCta } from './modal-cta';
import { ModalTitle } from './modal-title';

import { UnsavedChangesPromptService } from 'app/services';
import { Icon } from 'app/shared/icon';

/**
 * @deprecated Use `ModalDialog` from `src/app/shared/modal/modal/modal-dialog` (`<stby-modal-dialog>`) instead.
 * This component uses old design and will be removed.
 */
@Component({
    selector: 'stby-modal',
    styleUrl: './modal.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './modal.html',
    imports: [Icon, CdkScrollable],
})
export class Modal implements OnDestroy {
    private unsavedChangesPromptService = inject(UnsavedChangesPromptService);

    readonly allowWide = input(false, { transform: booleanAttribute });
    readonly allowOverflow = input(false, { transform: booleanAttribute });
    readonly denyClose = input(false, { transform: booleanAttribute });
    readonly closeOnlyByButton = input(false, { transform: booleanAttribute });

    readonly modalOpen = model(false);

    readonly size = input('');

    readonly checkHasChangesBeforeClose = input<boolean | (() => boolean)>();
    readonly closeModalPrompt = input<() => void>();

    private modalTitleQuery = contentChildren(ModalTitle);
    private modalContentQuery = contentChildren(ModalContent);
    private modalCtaQuery = contentChildren(ModalCta);
    modalTitleHasContent = computed(() => this.modalTitleQuery().length > 0);
    modalContentHasContent = computed(() => this.modalContentQuery().length > 0);
    modalCtaHasContent = computed(() => this.modalCtaQuery().length > 0);

    constructor() {
        effect(() => {
            if (this.modalOpen()) {
                this.addFreezeClass();
            } else {
                this.removeFreezeClass();
            }
        });
    }

    @HostListener('document:keydown.escape', ['$event']) onKeydownHandler(_: Event) {
        this.handleModalClose();
    }

    ngOnDestroy() {
        this.removeFreezeClass();
    }

    closeModal(): void {
        this.modalOpen.set(false);
    }

    handleModalClose(event?: MouseEvent) {
        // Do not close modal when click is not on the backdrop (container)
        if (event && event.target !== event.currentTarget) return;

        if (this.denyClose() || this.closeOnlyByButton()) return;
        this.promptCloseIfChanges();
    }

    onClickCloseButton() {
        this.promptCloseIfChanges();
    }

    private addFreezeClass(): void {
        document.body.classList.add('freeze');
    }

    private removeFreezeClass(): void {
        document.body.classList.remove('freeze');
    }

    toggleFreezeClass(): void {
        if (this.modalOpen()) {
            this.addFreezeClass();
        } else {
            this.removeFreezeClass();
        }
    }

    openModal(): void {
        this.modalOpen.set(true);
    }

    private promptCloseIfChanges() {
        const checkHasChanges = this.checkHasChangesBeforeClose();
        if (checkHasChanges) {
            this.promptClose((yes) => {
                if (yes) {
                    this.closeModal();
                }
            });
        } else {
            this.closeModal();
        }
    }

    promptClose(callback: (yes: boolean) => void) {
        const closeModalPrompt = this.closeModalPrompt();
        if (closeModalPrompt) {
            closeModalPrompt();
        } else {
            this.unsavedChangesPromptService.generatePrompt(
                'media-modal',
                () => callback(true),
                () =>
                    setTimeout(() => {
                        this.addFreezeClass();
                    }, 100),
            );
        }
    }
}
